import { Component, computed, effect, HostListener, inject, input, model, signal, untracked } from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { Tooltip } from "primeng/tooltip";
import { TranslatePipe } from "@ngx-translate/core";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { ActionsNavService } from "../../services/actions-nav.service";
import { actionsNavAnimation } from "../../animations/actions-nav.animation";
import { IActionsNavItem } from "../../interfaces/actions-nav-item.interface";

@Component({
  selector: "app-actions-nav",
  templateUrl: "./actions-nav.component.html",
  styleUrls: ["./actions-nav.component.scss"],
  animations: [actionsNavAnimation],
  imports: [Tooltip, TranslatePipe, SvgIconComponent, NgTemplateOutlet],
})
export class ActionsNavComponent {
  public readonly name = input.required<string>();
  public readonly defaultExpandedBy = input<string>();
  public readonly expandedBy = model<string>();
  public readonly actionsNavService = inject(ActionsNavService);
  public readonly state = computed(() => this.actionsNavService.getTrackedNavState(this.name()));
  public readonly items = computed(() => Array.from(this.state().values()));
  public readonly expandedByItem = computed(() => {
    const expandedBy = this.expandedBy();
    return expandedBy ? this.state().get(expandedBy) : null;
  });

  /** True when viewport width ≤ 1024 px (tablet / mobile) */
  public readonly isSmallScreen = signal(this.checkScreen());

  constructor() {
    effect(() => {
      const name = this.name();
      const pending = this.actionsNavService.getOpenRequest(name);
      if (!pending || !this.state().has(pending.itemId)) return;
      untracked(() => {
        this.expandedBy.set(pending.itemId);
        this.actionsNavService.consumeOpenRequest(name, pending.request);
      });
    });
    effect(() => {
      const name = this.name();
      const pending = this.actionsNavService.getCloseRequest(name);
      if (!pending || pending.itemId !== this.expandedBy()) return;
      untracked(() => {
        this.expandedBy.set(undefined);
        this.actionsNavService.consumeCloseRequest(name, pending.request);
      });
    });
    effect(() => {
      const defaultExpandedBy = this.defaultExpandedBy();
      const items = this.items();
      const isSmall = this.isSmallScreen();

      if (!defaultExpandedBy || items.length) {
        return;
      }

      // Only expand by default on large screens
      if (!isSmall) {
        this.expandedBy.set(defaultExpandedBy);
      } else {
        this.expandedBy.set(undefined);
      }
    });
  }

  /** Re-evaluate mobile breakpoint on window resize */
  @HostListener("window:resize")
  onResize(): void {
    this.isSmallScreen.set(this.checkScreen());
  }

  /** Switches the expanded item, asking the currently expanded item's `canDeactivate` first. */
  async selectItem(item: IActionsNavItem): Promise<void> {
    if (item.id === this.expandedBy()) return;
    const current = this.expandedByItem();
    if (current?.canDeactivate && !(await current.canDeactivate())) return;
    this.expandedBy.set(item.id);
  }

  private checkScreen(): boolean {
    return typeof window !== "undefined" && window.innerWidth <= 1024;
  }
}
